import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { analytics } from "./analytics";
import { reportWebVitals } from "./analytics/webVitals";

/**
 * Consent for the two categories of third-party script this app can load. Both are optional and
 * off by default — the app itself (signing in, playing, admin) works identically either way.
 * `null` means "not answered yet", which is distinct from "answered no": it's what keeps the
 * banner on screen.
 */
export interface ConsentPreferences {
  analytics: boolean;
  ads: boolean;
}

interface ConsentValue {
  preferences: ConsentPreferences | null;
  acceptAll: () => void;
  rejectAll: () => void;
  save: (preferences: ConsentPreferences) => void;
}

const STORAGE_KEY = "lastman:consent";
const ConsentContext = createContext<ConsentValue | null>(null);

function readStoredConsent(): ConsentPreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentPreferences>;
    if (typeof parsed.analytics !== "boolean" || typeof parsed.ads !== "boolean") return null;
    return { analytics: parsed.analytics, ads: parsed.ads };
  } catch {
    // Private browsing can throw on localStorage access, and a corrupted value shouldn't crash
    // the app — either way, treat it as "not answered yet" and show the banner again.
    return null;
  }
}

/**
 * Owns the visitor's consent choice and is the single place that flips analytics on. Nothing
 * elsewhere calls `analytics.setEnabled()` — this is it.
 *
 * Ad rendering (lib/promo/PromoSlot.tsx) reads `preferences.ads` directly rather than going through
 * the analytics manager, since ads and analytics are independent categories a visitor can accept
 * separately.
 */
export function ConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(() =>
    readStoredConsent(),
  );

  useEffect(() => {
    if (!preferences) return;
    analytics.setEnabled(preferences.analytics);
    if (preferences.analytics) reportWebVitals();
  }, [preferences]);

  const save = (next: ConsentPreferences) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Consent still applies for this session even if it can't be persisted.
    }
    setPreferences(next);
  };

  const value = useMemo<ConsentValue>(
    () => ({
      preferences,
      acceptAll: () => save({ analytics: true, ads: true }),
      rejectAll: () => save({ analytics: false, ads: false }),
      save,
    }),
    [preferences],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent(): ConsentValue {
  const context = useContext(ConsentContext);
  if (!context) throw new Error("useConsent must be used inside ConsentProvider.");
  return context;
}
